import React, { useState, useEffect } from 'react';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, getWhatsAppIntegrations, getUserChannelAccess, updateUserChannelAccess, getOrgDepartments } from '../utils/api';
import { ar, en } from '../utils/translations';
import { COUNTRY_CODES, splitPhoneAndCode } from '../utils/countryCodes';
import { FaUserPlus, FaEdit, FaTrash, FaSpinner, FaCheckCircle, FaUserFriends, FaBullhorn, FaKey, FaMoneyBillWave, FaTools, FaHeadset, FaCogs, FaSearch, FaList, FaTable, FaShieldAlt, FaTimes, FaStar, FaChevronLeft, FaChevronRight, FaClipboardList, FaInfoCircle, FaPhone, FaLock, FaUnlock, FaCopy, FaHandshake, FaBuilding, FaFileContract, FaChartLine, FaBalanceScale, FaUserTie } from 'react-icons/fa';

// ─── Permission definitions ────────────────────────────────────────────────────
// Messaging group: keys are wa_* today (WhatsApp-first). New marketing/ops channels can map to the same flags or add keys alongside.
const PERMISSION_GROUPS = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    label_ar: 'واتساب',
    color: '#25D366',
    permissions: [
      { key: 'wa_inbox',           label: 'Inbox & Reply',       label_ar: 'الرسائل والرد',       desc: 'Chats on the org’s connected lines', desc_ar: 'المحادثات على أرقام المنظمة المتصلة' },
      { key: 'wa_tools_use',       label: 'Chat tools — use',    label_ar: 'أدوات المحادثة — استخدام', desc: 'Same Tools area: apply saved replies & interactive flows (view-only)', desc_ar: 'نفس قسم «الأدوات»: استخدام الردود والتفاعلية دون تعديل التعريفات' },
      { key: 'wa_tools_manage',    label: 'Chat tools — manage', label_ar: 'أدوات المحادثة — إدارة',   desc: 'Same Tools area: add, edit, or remove replies & interactive flows', desc_ar: 'نفس قسم «الأدوات»: إضافة وتعديل وحذف الردود والتفاعلية' },
      { key: 'wa_campaigns_view',  label: 'View Campaigns',      label_ar: 'عرض الحملات',          desc: 'Campaign history & reports', desc_ar: 'سجل الحملات والتقارير' },
      { key: 'wa_campaigns_create',label: 'Create Campaigns',    label_ar: 'إنشاء الحملات',        desc: 'Build and send campaigns', desc_ar: 'إنشاء وإرسال الحملات' },
      { key: 'wa_templates_manage',label: 'Template management', label_ar: 'إدارة القوالب',        desc: 'Approved message templates (e.g. Meta; more channels later)', desc_ar: 'قوالب معتمدة — إنشاء وتعديل' },
      { key: 'wa_insights',        label: 'Analytics',           label_ar: 'التحليلات',            desc: 'WhatsApp analytics', desc_ar: 'تحليلات واتساب' },
    ]
  },
  {
    key: 'property',
    label: 'Properties & Units',
    label_ar: 'العقارات والوحدات',
    color: '#f97316',
    permissions: [
      { key: 'properties_view', label: 'View Properties', label_ar: 'عرض العقارات',      desc: 'Property registry', desc_ar: 'سجل العقارات' },
      { key: 'units_view',      label: 'View Units',      label_ar: 'عرض الوحدات',        desc: 'Units & listings', desc_ar: 'الوحدات والإعلانات' },
      { key: 'units_manage',    label: 'Manage Units',    label_ar: 'إدارة الوحدات',      desc: 'Listings, deposits, status', desc_ar: 'الإعلانات والعربون والحالة' },
    ]
  },
  {
    key: 'deals_clients',
    label: 'Deals & Clients',
    label_ar: 'الصفقات والعملاء',
    color: '#16a34a',
    permissions: [
      { key: 'clients_view',   label: 'View Clients',   label_ar: 'عرض العملاء',   desc: 'Client & prospect records', desc_ar: 'سجلات العملاء' },
      { key: 'clients_manage', label: 'Manage Clients', label_ar: 'إدارة العملاء', desc: 'Create & edit clients', desc_ar: 'إنشاء وتعديل العملاء' },
      { key: 'deals_view',     label: 'View Deals',     label_ar: 'عرض الصفقات',   desc: 'Deals pipeline', desc_ar: 'خط الصفقات' },
      { key: 'deals_manage',   label: 'Manage Deals',   label_ar: 'إدارة الصفقات', desc: 'Create & move deals', desc_ar: 'إنشاء وتقديم الصفقات' },
    ]
  },
];

/** Sensitive / backup access — shown last as optional delegation (not tied to one channel). */
const DELEGATION_GROUP = {
  key: 'delegation',
  label: 'Delegation',
  label_ar: 'التفويض',
  color: '#6366f1',
  permissions: [
    { key: 'wa_supervisor', label: 'Supervisor View', label_ar: 'عرض الإشراف', desc: 'Team queues & oversight', desc_ar: 'قائمة انتظار الفريق والإشراف' },
    { key: 'wa_inbox_triage', label: 'Inbox triage', label_ar: 'إدارة طابور الوارد', desc: 'Reassign, auto-assign, bulk mark read, dismiss follow-up (owner/manager have this by default)', desc_ar: 'إعادة التعيين والتعيين التلقائي وتمييز المحدد كمقروء وإغلاق المتابعة (المالك والمدير يملكونها تلقائياً)' },
    { key: 'wa_settings', label: 'Integration Settings', label_ar: 'إعدادات التكامل', desc: 'Manage connected channels & credentials', desc_ar: 'إدارة القنوات المتصلة وبيانات الاعتماد' },
  ],
};

const ALL_PERMISSION_KEYS = [
  ...PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key)),
  ...DELEGATION_GROUP.permissions.map((p) => p.key),
];
const EMPTY_PERMISSIONS = Object.fromEntries(ALL_PERMISSION_KEYS.map(k => [k, false]));

const PERMISSION_PRESETS = [
  {
    key: 'marketing', icon: '📣',
    label: 'Marketing', label_ar: 'التسويق',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, wa_tools_manage: true, wa_campaigns_view: true, wa_campaigns_create: true, wa_templates_manage: true, wa_insights: true, units_view: true, units_manage: true, clients_view: true }
  },
  {
    key: 'client_support', icon: '🎧',
    label: 'Client Support', label_ar: 'دعم العملاء',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, clients_view: true }
  },
  {
    key: 'sales', icon: '🤝',
    label: 'Sales', label_ar: 'المبيعات',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, clients_view: true, clients_manage: true, deals_view: true, deals_manage: true }
  },
  {
    key: 'leasing', icon: '🔑',
    label: 'Leasing', label_ar: 'التأجير',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, units_view: true, deals_view: true, deals_manage: true, clients_view: true, clients_manage: true }
  },
  {
    key: 'collections', icon: '💰',
    label: 'Collections', label_ar: 'التحصيل',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, deals_view: true, clients_view: true }
  },
  {
    key: 'maintenance', icon: '🔧',
    label: 'Maintenance', label_ar: 'الصيانة',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, units_view: true, clients_view: true, properties_view: true }
  },
  {
    key: 'view_only', icon: '👁',
    label: 'View Only', label_ar: 'عرض فقط',
    permissions: { ...EMPTY_PERMISSIONS, units_view: true, deals_view: true, clients_view: true, properties_view: true }
  },
  {
    key: 'full_access', icon: '⭐',
    label: 'Full Access', label_ar: 'صلاحيات كاملة',
    permissions: { ...EMPTY_PERMISSIONS, wa_inbox: true, wa_tools_use: true, wa_tools_manage: true, wa_campaigns_view: true, wa_campaigns_create: true, wa_templates_manage: true, wa_insights: true, units_view: true, units_manage: true, deals_view: true, deals_manage: true, clients_view: true, clients_manage: true, properties_view: true }
  },
];

// Count active permissions for a given object
const countActivePermissions = (perms = {}) =>
  ALL_PERMISSION_KEYS.filter(k => perms[k]).length;

/** API may return JSONB as object or JSON string — normalize for forms. */
const parseEmployeePermissions = (employee) => {
  const raw = employee?.permissions;
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p !== null && !Array.isArray(p) ? p : {};
    } catch (_) {
      return {};
    }
  }
  return {};
};

/**
 * `last_visit_date` is PostgreSQL TIMESTAMP (no tz) from the server; values are UTC wall time
 * (Render). A bare `2026-04-16 11:59:52` string is parsed by browsers as *local* time, which
 * shifts Arabia (UTC+3) by −3h. Treat naive ISO-like strings as UTC, then format with locale.
 */
const parseLastVisitUtc = (val) => {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  if (/Z$/i.test(s)) return new Date(s);
  if (/[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) return new Date(s);
  const normalized = s.includes('T') ? s : s.replace(/\s+/, 'T');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalized)) {
    const utc = new Date(`${normalized}Z`);
    if (!Number.isNaN(utc.getTime())) return utc;
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/** Short last-sign-in line: Latin digits + numeric date + time (avoids long ar-SA short+short strings). */
const formatLastSignInDisplay = (val, language) => {
  if (!val) return language === 'ar' ? 'لم يسجل' : 'Never';
  const d = parseLastVisitUtc(val);
  if (!d) return language === 'ar' ? 'لم يسجل' : 'Never';
  const diffDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  const loc = language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-US';
  if (diffDays < 2) {
    return d.toLocaleString(loc, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString(loc, { month: 'short', day: 'numeric' });
};

// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────

const EmployeeManagement = ({ language = 'ar', userRole }) => {
  const t = language === 'ar' ? ar : en;
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'cards' | 'table'
  const [roleFilter, setRoleFilter] = useState(''); // '' | 'manager' | 'employee'
  const [sortBy, setSortBy] = useState('last_visit_date'); // column key
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [permissionsOpen, setPermissionsOpen] = useState(true); // expand/collapse permissions section
  /** Employee wizard: 1 profile → 2 access → 3 optional delegation */
  const [modalStep, setModalStep] = useState(1);
  const [permFilter, setPermFilter] = useState('all'); // all | whatsapp | property | deals_clients
  const [permSearchQuery, setPermSearchQuery] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [delegationPrompt, setDelegationPrompt] = useState(null); // { key, nextOn } for sensitive toggle confirm
  const [showZeroPermConfirm, setShowZeroPermConfirm] = useState(false);
  const [onboardingPayload, setOnboardingPayload] = useState(null); // after create
  // Channel access state for Step 4
  const [integrationNumbers, setIntegrationNumbers] = useState([]); // [{id, label}] from org integrations
  const [channelGrants, setChannelGrants] = useState([]); // phone_number_ids this employee can access
  const [channelAccessLoading, setChannelAccessLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    password: '',
    role: 'employee',
    departments: [],          // array of dept values
    permissions: { ...EMPTY_PERMISSIONS }
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState('+966');
  const [customDepts, setCustomDepts] = useState([]); // loaded from org

  // Global (platform) department definitions — always visible to all orgs
  const GLOBAL_DEPARTMENTS = [
    // ── Core focus ──────────────────────────────────────────────────────────
    { value: 'marketing',         label: language === 'ar' ? 'التسويق'       : 'Marketing',            icon: FaBullhorn,     color: '#f97316', isGlobal: true },
    { value: 'client_support',    label: language === 'ar' ? 'دعم العملاء'   : 'Client Support',       icon: FaHeadset,      color: '#0ea5e9', isGlobal: true },
    { value: 'sales',             label: language === 'ar' ? 'المبيعات'      : 'Sales',                icon: FaHandshake,    color: '#16a34a', isGlobal: true },
    // ── Operational ─────────────────────────────────────────────────────────
    { value: 'administration',    label: language === 'ar' ? 'الإدارة'       : 'Administration',       icon: FaUserTie,      color: '#6b7280', isGlobal: true },
    { value: 'leasing',           label: language === 'ar' ? 'التأجير'       : 'Leasing',              icon: FaKey,          color: '#a78bfa', isGlobal: true },
    { value: 'property_management', label: language === 'ar' ? 'إدارة الأملاك' : 'Property Management', icon: FaBuilding,   color: '#0d9488', isGlobal: true },
    { value: 'finance',           label: language === 'ar' ? 'المالية'       : 'Finance',              icon: FaChartLine,    color: '#eab308', isGlobal: true },
    { value: 'collections',       label: language === 'ar' ? 'التحصيل'       : 'Collections',          icon: FaMoneyBillWave, color: '#f59e0b', isGlobal: true },
    { value: 'maintenance',       label: language === 'ar' ? 'الصيانة'       : 'Maintenance',          icon: FaTools,        color: '#8b5cf6', isGlobal: true },
    { value: 'contracts',         label: language === 'ar' ? 'العقود'        : 'Contracts',            icon: FaFileContract, color: '#06b6d4', isGlobal: true },
    { value: 'advisory',          label: language === 'ar' ? 'الاستشارات'    : 'Advisory',             icon: FaBalanceScale, color: '#6366f1', isGlobal: true },
    { value: 'valuation',         label: language === 'ar' ? 'التقييم'       : 'Valuation',            icon: FaChartLine,    color: '#ec4899', isGlobal: true },
  ];

  // Merged list: globals first, then org custom (skip any custom that duplicates a global value)
  const departments = [
    ...GLOBAL_DEPARTMENTS,
    ...customDepts
      .filter(c => !GLOBAL_DEPARTMENTS.find(g => g.value === c.value))
      .map(c => ({
        value: c.value,
        label: language === 'ar' && c.label_ar ? c.label_ar : c.label,
        icon: FaCogs,
        color: c.color || '#6b7280',
        isCustom: true,
      }))
  ];

  const getDepartmentInfo = (deptValue) => {
    return departments.find(d => d.value === deptValue) || departments[0];
  };

  const loadCustomDepts = async () => {
    try {
      const res = await getOrgDepartments();
      setCustomDepts(Array.isArray(res.data?.departments) ? res.data.departments : []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    loadEmployees();
    loadCustomDepts();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await getEmployees();
      console.log('Employees response:', response);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      
      // If 403 (forbidden), user might not have permission
      if (error.response?.status === 403) {
        console.warn('Access denied - user may not have owner/manager role');
        setEmployees([]); // Set empty array instead of showing error
      } else if (error.response?.status === 404) {
        // Endpoint not found - set empty array
        setEmployees([]);
      } else {
        // Other errors - show alert
        alert(language === 'ar' ? 'فشل تحميل الموظفين' : 'Failed to load employees');
        setEmployees([]); // Still set empty array to prevent UI issues
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEmployee(null);
    setPhoneCountryCode('+966');
    setFormData({
      name: '',
      phone_number: '',
      password: '',
      role: 'employee',
      departments: [],
      permissions: { ...EMPTY_PERMISSIONS }
    });
    setPermissionsOpen(true);
    setModalStep(1);
    setPermFilter('all');
    setPermSearchQuery('');
    setFormErrors({});
    setDelegationPrompt(null);
    setShowZeroPermConfirm(false);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    const { code, local } = splitPhoneAndCode(employee.phone_number);
    setPhoneCountryCode(code);
    let depts = employee.department
      ? employee.department.split(',').map(d => d.trim()).filter(Boolean)
      : [];
    if (employee.role === 'manager') {
      depts = Array.from(new Set([...depts, 'administration']));
    }
    const savedPerms = parseEmployeePermissions(employee);
    setFormData({
      name: employee.name,
      phone_number: local,
      password: '',
      role: employee.role,
      departments: depts,
      permissions: { ...EMPTY_PERMISSIONS, ...savedPerms }
    });
    setPermissionsOpen(true);
    setModalStep(1);
    setPermFilter('all');
    setPermSearchQuery('');
    setFormErrors({});
    setDelegationPrompt(null);
    setShowZeroPermConfirm(false);
    setShowModal(true);
    // Load channel access grants for this employee (async, non-blocking)
    setChannelGrants([]);
    setChannelAccessLoading(true);
    Promise.all([
      getWhatsAppIntegrations().catch(() => null),
      getUserChannelAccess(employee.id).catch(() => null),
    ]).then(([intResp, accessResp]) => {
      const nums = Array.isArray(intResp?.data?.integrations)
        ? intResp.data.integrations.map(r => ({ id: String(r.phone_number_id || ''), label: String(r.business_display_name || r.phone_number_id || '') })).filter(r => r.id)
        : [];
      setIntegrationNumbers(nums);
      const grants = Array.isArray(accessResp?.data?.grants) ? accessResp.data.grants.map(g => g.channel_identifier) : [];
      setChannelGrants(grants);
    }).finally(() => setChannelAccessLoading(false));
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar'
      ? 'هل أنت متأكد من حذف هذا الموظف؟ سيتم منعه من تسجيل الدخول وإزالته من جميع القوائم. ملاحظة: ستبقى جميع محادثاته وسجلاته محفوظة في النظام.'
      : 'Are you sure you want to delete this employee? They will be blocked from logging in and removed from all lists. Note: all their conversations and records will remain saved in the system.')) {
      return;
    }

    try {
      await deleteEmployee(id);
      alert(language === 'ar' ? 'تم حذف الموظف بنجاح' : 'Employee deleted successfully');
      loadEmployees();
    } catch (error) {
      console.error('Error deactivating employee:', error);
      alert(language === 'ar' ? 'فشل حذف الموظف' : 'Failed to delete employee');
    }
  };

  const normalizePhoneDigits = (raw) => String(raw || '').replace(/[\s\-\(\)\+]/g, '');

  /** Managers always carry the administration department; deduped if already selected. */
  const departmentsCsvForRole = (deptValues, role) => {
    const list = Array.isArray(deptValues) ? [...deptValues] : [];
    if (String(role || '').toLowerCase() === 'manager') {
      const merged = Array.from(new Set([...list, 'administration']));
      return merged.length ? merged.join(',') : 'administration';
    }
    return list.length ? list.join(',') : 'administration';
  };

  const validateProfile = () => {
    const err = {};
    const name = String(formData.name || '').trim();
    if (!name) err.name = language === 'ar' ? 'أدخل الاسم' : 'Enter a name';
    const digits = normalizePhoneDigits(formData.phone_number);
    const fullDigits = phoneCountryCode.replace(/\D/g, '') + digits;
    if (!digits) {
      err.phone_number = language === 'ar' ? 'أدخل رقم الهاتف' : 'Enter a phone number';
    } else if (!/^\d+$/.test(digits)) {
      err.phone_number = language === 'ar' ? 'الرقم يجب أن يحتوي على أرقام فقط' : 'Use digits only';
    } else if (fullDigits.length < 8 || fullDigits.length > 15) {
      err.phone_number = language === 'ar' ? 'الطول يجب أن يكون بين 8 و 15 رقماً' : 'Length must be 8–15 digits';
    }
    if (!editingEmployee) {
      if (!formData.password || !String(formData.password).trim()) {
        err.password = language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required';
      } else if (String(formData.password).length < 6) {
        err.password = language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters';
      }
    } else if (formData.password && String(formData.password).length < 6) {
      err.password = language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters';
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const performSave = async (opts = {}) => {
    const forceZero = !!opts.forceZero;
    setFormErrors({});
    if (!validateProfile()) return;
    if (formData.role === 'employee' && countActivePermissions(formData.permissions) === 0 && !forceZero) {
      setShowZeroPermConfirm(true);
      return;
    }
    setShowZeroPermConfirm(false);

    try {
      const deptString = departmentsCsvForRole(formData.departments, formData.role);
      const phoneNorm = phoneCountryCode.replace(/\D/g, '') + normalizePhoneDigits(formData.phone_number).replace(/^0+/, '');

      if (editingEmployee) {
        const updateData = {
          name: String(formData.name).trim(),
          phone_number: phoneNorm,
          role: formData.role,
          department: deptString,
          permissions: formData.permissions
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateEmployee(editingEmployee.id, updateData);
        // Persist channel access grants (fire-and-forget — non-blocking to not hold up the save)
        if (editingEmployee.role === 'employee' || formData.role === 'employee') {
          updateUserChannelAccess(editingEmployee.id, channelGrants.map(id => ({ channel_type: 'whatsapp', channel_identifier: id, access_level: 'write' }))).catch(() => {});
        }
        alert(language === 'ar' ? 'تم تحديث العضو بنجاح' : 'Team member updated successfully');
        setShowModal(false);
        loadEmployees();
      } else {
        const created = await createEmployee({
          name: String(formData.name).trim(),
          phone_number: phoneNorm,
          password: formData.password,
          role: formData.role,
          department: deptString,
          permissions: formData.permissions
        });
        // Persist any channel grants set during wizard (needs the new user ID)
        const newId = created?.data?.id;
        if (newId && channelGrants.length > 0) {
          updateUserChannelAccess(newId, channelGrants.map(id => ({ channel_type: 'whatsapp', channel_identifier: id, access_level: 'write' }))).catch(() => {});
        }
      setShowModal(false);
        setOnboardingPayload({
          name: String(formData.name).trim(),
          phone: phoneNorm,
          passwordHint: language === 'ar'
            ? 'شارِكها بأمان واطلب تغييرها من «معلومات الحساب» بعد أول دخول.'
            : 'Share it securely; they should change it under Account after first sign-in.',
        });
      loadEmployees();
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      const errorMsg = error.response?.data?.error || error.message || (language === 'ar' ? 'فشل حفظ الموظف' : 'Failed to save employee');
      alert(errorMsg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await performSave({ forceZero: false });
  };

  const DELEGATION_KEYS = new Set(DELEGATION_GROUP.permissions.map((p) => p.key));

  const permissionMatchesSearch = (perm) => {
    const q = String(permSearchQuery || '').trim().toLowerCase();
    if (!q) return true;
    const blob = [
      perm.key,
      perm.label,
      perm.label_ar,
      perm.desc,
      perm.desc_ar,
    ]
      .join(' ')
      .toLowerCase();
    return blob.includes(q);
  };

  const handleDelegationToggle = (permKey, nextOn) => {
    if (!nextOn) {
      setFormData((fd) => ({
        ...fd,
        permissions: { ...fd.permissions, [permKey]: false },
      }));
      return;
    }
    setDelegationPrompt({ key: permKey, nextOn: true });
  };

  const confirmDelegationPrompt = () => {
    if (!delegationPrompt) return;
    const { key } = delegationPrompt;
    setFormData((fd) => ({
      ...fd,
      permissions: { ...fd.permissions, [key]: true },
    }));
    setDelegationPrompt(null);
  };

  /** Single permission row; optional sensitive delegation confirm when turning on. */
  const renderPermissionRow = (perm, group, { sensitiveDelegation } = {}) => {
    const on = !!formData.permissions[perm.key];
    const descColor = '#4a5568';
    const toggle = () => {
      if (sensitiveDelegation && DELEGATION_KEYS.has(perm.key)) {
        handleDelegationToggle(perm.key, !on);
        return;
      }
      setFormData((fd) => ({
        ...fd,
        permissions: { ...fd.permissions, [perm.key]: !fd.permissions[perm.key] },
      }));
    };
    return (
      <label
        key={perm.key}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '12px 14px',
          borderRadius: '10px',
          cursor: 'pointer',
          background: on ? `${group.color}10` : '#fafafa',
          border: `2px solid ${on ? group.color + '55' : '#e8eaed'}`,
          boxShadow: on ? `0 1px 0 ${group.color}22` : 'none',
          transition: 'all 0.12s ease',
        }}
      >
        <div
          role="presentation"
          onClick={(e) => {
            e.preventDefault();
            toggle();
          }}
          style={{
            width: '40px',
            height: '24px',
            borderRadius: '12px',
            flexShrink: 0,
            background: on ? group.color : '#cbd5e1',
            position: 'relative',
            transition: 'background 0.2s ease',
            marginTop: '2px',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '3px',
              left: on ? '20px' : '4px',
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.9rem', lineHeight: 1.35 }}>
            {language === 'ar' ? perm.label_ar : perm.label}
          </div>
          <div style={{ color: descColor, fontSize: '0.8rem', marginTop: '5px', lineHeight: 1.45 }}>
            {language === 'ar' ? perm.desc_ar : perm.desc}
          </div>
        </div>
      </label>
    );
  };

  const formatEmpDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', { dateStyle: 'medium' });
  };

  const canCreateManager = userRole === 'owner';
  const canDelete = userRole === 'owner';
  const isEmployeeWizard = formData.role === 'employee';
  const isRTL = language === 'ar';
  const NextIcon = isRTL ? FaChevronLeft : FaChevronRight;
  const BackIcon = isRTL ? FaChevronRight : FaChevronLeft;

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <FaSpinner style={{ fontSize: '2rem', color: '#94a3b8', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '20px' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
      </div>
    );
  }

  // Parse employee's comma-separated department string into an array
  const getEmployeeDepts = (e) =>
    (e.department || 'administration').split(',').map(d => d.trim()).filter(Boolean);

  /** Last *panel* sign-in recency — not live/online status */
  const getActivityStatus = (lastVisit) => {
    if (!lastVisit) return 'never';
    const d = parseLastVisitUtc(lastVisit);
    if (!d) return 'never';
    const diffDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays < 1) return 'today';
    if (diffDays < 7) return 'recent';
    return 'inactive';
  };

  const signInRecencyDot = (status) => {
    if (status === 'today') {
      return {
        color: '#0369a1',
        title: language === 'ar'
          ? 'آخر تسجيل دخول للوحة كان اليوم (ليس «متصل الآن»)'
          : 'Last panel sign-in was today (not “online now”)',
      };
    }
    if (status === 'recent') {
      return {
        color: '#d97706',
        title: language === 'ar'
          ? 'آخر تسجيل دخول خلال آخر 7 أيام'
          : 'Last sign-in within the past 7 days',
      };
    }
    if (status === 'never') {
      return {
        color: '#d1d5db',
        title: language === 'ar' ? 'لم يسجّل دخولاً بعد' : 'Never signed in',
      };
    }
    return {
      color: '#d1d5db',
      title: language === 'ar'
        ? 'آخر تسجيل دخول منذ أكثر من 7 أيام'
        : 'Last sign-in more than 7 days ago',
    };
  };

  const formatLastLogin = (lastVisit) => formatLastSignInDisplay(lastVisit, language);

  // Permission count helper
  const countPermissions = (permissions) => {
    if (!permissions || typeof permissions !== 'object') return 0;
    return Object.values(permissions).filter(Boolean).length;
  };

  // Sort handler
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: '10px', marginLeft: '4px' }}>↕</span>;
    return <span style={{ fontSize: '10px', marginLeft: '4px', color: '#f97316' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Filter employees by department, role AND search query (name, phone, role, department)
  const filteredEmployees = employees
    .filter(e => {
    const matchesDepartment = !departmentFilter || getEmployeeDepts(e).includes(departmentFilter);
      const matchesRole = !roleFilter || e.role === roleFilter;
      const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
        e.name.toLowerCase().includes(q) ||
        e.phone_number.includes(q) ||
        (e.role || '').toLowerCase().includes(q) ||
        (e.department || '').toLowerCase().includes(q);
      return matchesDepartment && matchesRole && matchesSearch;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'last_visit_date') {
        aVal = a.last_visit_date ? (parseLastVisitUtc(a.last_visit_date)?.getTime() ?? 0) : 0;
        bVal = b.last_visit_date ? (parseLastVisitUtc(b.last_visit_date)?.getTime() ?? 0) : 0;
      } else if (sortBy === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      } else if (sortBy === 'role') {
        aVal = a.role || '';
        bVal = b.role || '';
      } else if (sortBy === 'department') {
        aVal = a.department || '';
        bVal = b.department || '';
      } else {
        return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
  });

  // Group employees by department for summary (an employee in multiple depts counts in each)
  const departmentCounts = departments.map(dept => ({
    ...dept,
    count: employees.filter(e => getEmployeeDepts(e).includes(dept.value)).length
  })).filter(d => d.count > 0 || d.value === 'administration');

  return (
    <div>
      <div className="card">
        <div className="employee-management-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaUserFriends style={{ color: '#f97316' }} />
            {language === 'ar' ? 'إدارة الفريق' : 'Team Management'}
          </h2>
          <button
            onClick={handleCreate}
            style={{
              padding: '10px 20px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            <FaUserPlus />
            {language === 'ar' ? 'إضافة عضو' : 'Add Member'}
          </button>
        </div>

        {/* Quick Stats Summary Bar */}
        {employees.length > 0 && (() => {
          const managers = employees.filter(e => e.role === 'manager').length;
          const emps = employees.filter(e => e.role === 'employee').length;
          const activeToday = employees.filter(e => getActivityStatus(e.last_visit_date) === 'today').length;
          const inactive7 = employees.filter(e => getActivityStatus(e.last_visit_date) === 'inactive').length;
          const pills = [
            { label: language === 'ar' ? `👥 ${employees.length} عضو` : `👥 ${employees.length} members`, color: '#374151', bg: '#f3f4f6' },
            { label: language === 'ar' ? `🔑 ${managers} مدير · ${emps} موظف` : `🔑 ${managers} manager · ${emps} employee`, color: '#92400e', bg: '#fef3c7' },
            { label: language === 'ar' ? `${activeToday} — تسجيل دخول خلال 24 ساعة` : `${activeToday} signed in within 24h`, color: '#065f46', bg: '#d1fae5' },
            ...(inactive7 > 0 ? [{ label: language === 'ar' ? `${inactive7} — بدون تسجيل دخول منذ 7 أيام فأكثر` : `${inactive7} no sign-in for 7+ days`, color: '#991b1b', bg: '#fee2e2' }] : []),
          ];
          return (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {pills.map((p, i) => (
                <span key={i} style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: p.bg, color: p.color }}>
                  {p.label}
                </span>
              ))}
            </div>
          );
        })()}

        {/* Search and View Controls */}
        {employees.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Input */}
            <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '280px' }}>
              <FaSearch style={{ 
                position: 'absolute', 
                left: language === 'ar' ? 'auto' : '10px',
                right: language === 'ar' ? '10px' : 'auto',
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#9ca3af',
                fontSize: '13px'
              }} />
              <input
                type="text"
                placeholder={language === 'ar' ? 'بحث بالاسم، الرقم، الدور، القسم...' : 'Search name, phone, role, dept...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  paddingLeft: language === 'ar' ? '12px' : '34px',
                  paddingRight: language === 'ar' ? '34px' : '12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '13px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Role Filter */}
            <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
              {[
                { val: '', label: language === 'ar' ? 'الكل' : 'All' },
                { val: 'manager', label: language === 'ar' ? 'مدير' : 'Manager' },
                { val: 'employee', label: language === 'ar' ? 'موظف' : 'Employee' },
              ].map(r => (
                <button key={r.val} onClick={() => setRoleFilter(r.val)} style={{
                  padding: '5px 12px', fontSize: '12px', fontWeight: '500', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  background: roleFilter === r.val ? '#fff' : 'transparent',
                  color: roleFilter === r.val ? '#f97316' : '#6b7280',
                  boxShadow: roleFilter === r.val ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>{r.label}</button>
              ))}
            </div>

            {/* View Toggle */}
            <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
              <button onClick={() => setViewMode('cards')} title={language === 'ar' ? 'بطاقات' : 'Cards'} style={{
                padding: '5px 10px', background: viewMode === 'cards' ? '#fff' : 'transparent', border: 'none',
                borderRadius: '6px', cursor: 'pointer', color: viewMode === 'cards' ? '#f97316' : '#6b7280',
                boxShadow: viewMode === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px'
              }}>
                <FaList style={{ fontSize: '11px' }} />{language === 'ar' ? 'بطاقات' : 'Cards'}
              </button>
              <button onClick={() => setViewMode('table')} title={language === 'ar' ? 'جدول' : 'Table'} style={{
                padding: '5px 10px', background: viewMode === 'table' ? '#fff' : 'transparent', border: 'none',
                borderRadius: '6px', cursor: 'pointer', color: viewMode === 'table' ? '#f97316' : '#6b7280',
                boxShadow: viewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px'
              }}>
                <FaTable style={{ fontSize: '11px' }} />{language === 'ar' ? 'جدول' : 'Table'}
              </button>
            </div>
            
            <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: 'auto' }}>
              {filteredEmployees.length} / {employees.length} {language === 'ar' ? 'عضو' : 'members'}
            </span>
          </div>
        )}

        {/* Department Filter Pills */}
        {employees.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setDepartmentFilter('')}
              style={{
                padding: '8px 16px',
                backgroundColor: !departmentFilter ? '#f97316' : '#f3f4f6',
                color: !departmentFilter ? 'white' : '#374151',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {language === 'ar' ? 'الكل' : 'All'} ({employees.length})
            </button>
            {departmentCounts.map(dept => {
              const DeptIcon = dept.icon;
              return (
                <button
                  key={dept.value}
                  onClick={() => setDepartmentFilter(departmentFilter === dept.value ? '' : dept.value)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: departmentFilter === dept.value ? dept.color : '#f3f4f6',
                    color: departmentFilter === dept.value ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <DeptIcon style={{ fontSize: '12px' }} />
                  {dept.label} ({dept.count})
                </button>
              );
            })}
          </div>
        )}

        {filteredEmployees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <FaUserFriends style={{ fontSize: '3rem', color: '#ccc', marginBottom: '20px' }} />
            <p>{searchQuery 
              ? (language === 'ar' ? 'لا توجد نتائج للبحث' : 'No search results')
              : (language === 'ar' ? 'لا يوجد أعضاء في الفريق' : 'No team members')
            }</p>
          </div>
        ) : viewMode === 'table' ? (
          /* TABLE VIEW */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {[
                    { key: 'name', label: language === 'ar' ? 'الاسم' : 'Name' },
                    { key: 'phone', label: language === 'ar' ? 'الهاتف' : 'Phone', noSort: true },
                    { key: 'department', label: language === 'ar' ? 'القسم' : 'Department' },
                    { key: 'role', label: language === 'ar' ? 'الدور' : 'Role' },
                    { key: 'permissions', label: language === 'ar' ? 'صلاحيات' : 'Permissions', noSort: true },
                    { key: 'last_visit_date', label: language === 'ar' ? 'آخر تسجيل دخول' : 'Last sign-in' },
                    { key: 'actions', label: language === 'ar' ? 'إجراءات' : 'Actions', noSort: true, center: true },
                  ].map(col => (
                    <th key={col.key}
                      onClick={col.noSort ? undefined : () => handleSort(col.key)}
                      style={{
                        padding: '8px 12px',
                        textAlign: col.center ? 'center' : (language === 'ar' ? 'right' : 'left'),
                        fontWeight: '600', color: '#374151',
                        cursor: col.noSort ? 'default' : 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}>
                      {col.label}{!col.noSort && <SortIcon col={col.key} />}
                  </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => {
                  const deptInfo = getDepartmentInfo(employee.department || 'administration');
                  const DeptIcon = deptInfo.icon;
                  return (
                    <tr key={employee.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '7px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {(() => {
                          const gradients = [
                            'linear-gradient(135deg, #292524 0%, #44403c 100%)',
                            'linear-gradient(135deg, #7c2d12 0%, #c2410c 100%)',
                            'linear-gradient(135deg, #9f1239 0%, #e11d48 100%)',
                            'linear-gradient(135deg, #6b21a8 0%, #a855f7 100%)',
                            'linear-gradient(135deg, #78350f 0%, #b45309 100%)',
                            'linear-gradient(135deg, #881337 0%, #be123c 100%)',
                            'linear-gradient(135deg, #451a03 0%, #92400e 100%)',
                            'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
                          ];
                          const colorIdx = employee.name ? (employee.name.charCodeAt(0) + (employee.name.charCodeAt(1) || 0)) % gradients.length : 0;
                          const initials = employee.name ? employee.name.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase() : '?';
                          return (
                            <div style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: gradients[colorIdx],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '600',
                              letterSpacing: '0.5px',
                              flexShrink: 0
                            }}>
                              {initials}
                            </div>
                          );
                        })()}
                        <span style={{ fontWeight: '500', color: '#1f2937', fontSize: '13px' }}>{employee.name}</span>
                      </td>
                      <td style={{ padding: '7px 12px', color: '#6b7280', fontSize: '12px' }}>{employee.phone_number}</td>
                      <td style={{ padding: '7px 12px' }}>
                        {getEmployeeDepts(employee).map(dept => {
                          const di = getDepartmentInfo(dept);
                          return (
                            <span key={dept} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '3px',
                              padding: '1px 6px', borderRadius: '10px',
                              background: `${di.color}15`, color: di.color,
                              fontSize: '11px', marginRight: '3px'
                            }}>{di.label}</span>
                          );
                        })}
                      </td>
                      <td style={{ padding: '7px 12px' }}>
                        <span style={{
                          padding: '2px 7px', borderRadius: '12px',
                          background: employee.role === 'manager' ? '#fef3c7' : '#f3f4f6',
                          color: employee.role === 'manager' ? '#92400e' : '#6b7280',
                          fontSize: '11px', fontWeight: '500'
                        }}>
                          {employee.role === 'manager' 
                            ? (language === 'ar' ? 'مدير' : 'Manager')
                            : (language === 'ar' ? 'موظف' : 'Employee')}
                        </span>
                      </td>
                      <td style={{ padding: '7px 12px' }}>
                        {(() => {
                          if (employee.role === 'owner') {
                            const title = language === 'ar'
                              ? 'مالك المؤسسة: صلاحيات كاملة دون الاعتماد على قائمة الصلاحيات.'
                              : 'Organization owner: full access; permission toggles do not apply.';
                            return (
                              <span style={{
                                padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                                background: '#fef3c7', color: '#92400e'
                              }} title={title}>
                                {language === 'ar' ? 'صلاحيات كاملة (مالك)' : 'Full access (owner)'}
                              </span>
                            );
                          }
                          if (employee.role === 'manager') {
                            const title = language === 'ar'
                              ? 'المدير يملك صلاحيات واسعة (إدارة الفريق والوصول للبيانات) بغض النظر عن عدد الخانات في الجدول.'
                              : 'Managers have broad access (team management and data); not limited by the permission checklist.';
                            return (
                              <span style={{
                                padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                                background: '#fef3c7', color: '#92400e'
                              }} title={title}>
                                {language === 'ar' ? 'صلاحيات كاملة (مدير)' : 'Full access (manager)'}
                              </span>
                            );
                          }
                          const perms = typeof employee.permissions === 'string'
                            ? (() => { try { return JSON.parse(employee.permissions); } catch { return {}; } })()
                            : (employee.permissions || {});
                          const count = countPermissions(perms);
                          return (
                            <span style={{
                              padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                              background: count > 0 ? '#eff6ff' : '#f3f4f6',
                              color: count > 0 ? '#1d4ed8' : '#9ca3af'
                            }}
                              title={Object.entries(perms).filter(([, v]) => v).map(([k]) => k).join(', ') || (language === 'ar' ? 'لا صلاحيات مفعّلة' : 'No permissions enabled')}
                            >
                              {count > 0
                                ? `${count} ${language === 'ar' ? 'صلاحية' : 'perms'}`
                                : (language === 'ar' ? 'بدون صلاحيات' : 'No permissions')}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: '11px',
                          color: getActivityStatus(employee.last_visit_date) === 'inactive' ? '#dc2626'
                            : getActivityStatus(employee.last_visit_date) === 'today' ? '#16a34a' : '#6b7280',
                          fontWeight: getActivityStatus(employee.last_visit_date) === 'today' ? '600' : '400'
                        }}>
                          {formatLastLogin(employee.last_visit_date)}
                        </span>
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEdit(employee)}
                            style={{ padding: '4px 8px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}
                            title={language === 'ar' ? 'تعديل' : 'Edit'}
                          >
                            <FaEdit />
                          </button>
                          {canDelete && employee.role !== 'owner' && (
                            <button
                              onClick={() => handleDelete(employee.id)}
                              style={{ padding: '4px 8px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}
                              title={language === 'ar' ? 'حذف' : 'Delete'}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* CARDS VIEW - Advanced Professional Design */
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '16px' 
          }}>
            {filteredEmployees.map((employee) => {
              const deptInfo = getDepartmentInfo(employee.department || 'administration');
              const DeptIcon = deptInfo.icon;
              // Warm & elegant palette (no blues, no greens)
              const premiumGradients = [
                { bg: 'linear-gradient(135deg, #57534e 0%, #78716c 100%)', ring: '#a8a29e' }, // Warm Gray
                { bg: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', ring: '#fb923c' }, // Bright Orange
                { bg: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)', ring: '#fb7185' }, // Rose
                { bg: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)', ring: '#d8b4fe' }, // Purple
                { bg: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', ring: '#fbbf24' }, // Amber
                { bg: 'linear-gradient(135deg, #be123c 0%, #e11d48 100%)', ring: '#fb7185' }, // Burgundy
                { bg: 'linear-gradient(135deg, #c2410c 0%, #ea580c 100%)', ring: '#fb923c' }, // Terracotta
                { bg: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', ring: '#c084fc' }, // Violet
              ];
              const colorIndex = employee.name ? 
                (employee.name.charCodeAt(0) + (employee.name.charCodeAt(1) || 0)) % premiumGradients.length : 0;
              const avatarStyle = premiumGradients[colorIndex];
              const initials = employee.name 
                ? employee.name.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase()
                : '?';
              
              return (
              <div
                key={employee.id}
                style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                  padding: '20px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  {/* Top Section - Avatar & Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                    {/* Advanced Avatar with Ring */}
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      padding: '3px',
                      background: `linear-gradient(135deg, ${avatarStyle.ring}40 0%, ${avatarStyle.ring} 100%)`,
                      position: 'relative',
                      flexShrink: 0
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: avatarStyle.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                        fontSize: '1.15rem',
                        fontWeight: '600',
                        letterSpacing: '1px',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {initials}
                      </div>
                      {/* Manager Badge */}
                      {employee.role === 'manager' && (
                        <div style={{
                          position: 'absolute',
                          bottom: '0px',
                          right: '0px',
                          width: '18px',
                          height: '18px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }}>
                          <FaStar style={{ fontSize: '8px', color: 'white' }} />
                        </div>
                      )}
                    </div>
                    
                    {/* Name & Contact */}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '1.15rem', 
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {employee.name}
                      </h3>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        color: '#6b7280',
                        fontSize: '0.9rem'
                      }}>
                        <FaPhone style={{ fontSize: '11px', color: '#9ca3af' }} />
                        {employee.phone_number}
                      </div>
                    </div>
                  </div>
                  
                  {/* Department badges (multi) & Role badge */}
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {getEmployeeDepts(employee).map(dv => {
                      const di = getDepartmentInfo(dv);
                      const DI = di.icon;
                      return (
                        <span key={dv} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '4px 10px', borderRadius: '20px',
                          background: `${di.color}15`, color: di.color,
                          fontSize: '0.78rem', fontWeight: '500'
                        }}>
                          <DI style={{ fontSize: '10px' }} />
                          {di.label}
                    </span>
                      );
                    })}
                    <span style={{
                      padding: '4px 10px', borderRadius: '20px',
                      background: employee.role === 'manager' ? '#fef3c7' : '#f3f4f6',
                      color: employee.role === 'manager' ? '#92400e' : '#6b7280',
                      fontSize: '0.78rem', fontWeight: '500'
                    }}>
                      {employee.role === 'manager' 
                        ? (language === 'ar' ? 'مدير' : 'Manager')
                        : (language === 'ar' ? 'موظف' : 'Employee')}
                    </span>
                  </div>
                  {/* Permission summary (employees: checklist; managers/owners: full access) */}
                    <div style={{ marginBottom: '12px' }}>
                    {employee.role === 'owner' && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 10px', borderRadius: '20px',
                        background: '#fef3c7', color: '#92400e',
                        fontSize: '0.75rem', fontWeight: '500'
                      }}>
                        {language === 'ar' ? 'صلاحيات كاملة (مالك)' : 'Full access (owner)'}
                      </span>
                    )}
                    {employee.role === 'manager' && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 10px', borderRadius: '20px',
                        background: '#fef3c7', color: '#92400e',
                        fontSize: '0.75rem', fontWeight: '500'
                      }}>
                        {language === 'ar' ? 'صلاحيات كاملة (مدير)' : 'Full access (manager)'}
                      </span>
                    )}
                    {employee.role === 'employee' && (() => {
                        const perms = (employee.permissions && typeof employee.permissions === 'object') ? employee.permissions : {};
                        const active = countActivePermissions(perms);
                        return (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '3px 10px', borderRadius: '20px',
                            background: active > 0 ? '#ecfdf5' : '#fef2f2',
                            color: active > 0 ? '#15803d' : '#dc2626',
                            fontSize: '0.75rem', fontWeight: '500'
                          }}>
                            {active > 0 ? <FaUnlock style={{ fontSize: '10px' }} /> : <FaLock style={{ fontSize: '10px' }} />}
                            {active > 0
                              ? `${active} ${language === 'ar' ? 'صلاحية' : `permission${active > 1 ? 's' : ''}`}`
                              : (language === 'ar' ? 'بدون صلاحيات' : 'No permissions')}
                          </span>
                        );
                      })()}
                    </div>
                  
                  {/* Action Buttons */}
                  <div style={{ 
                      display: 'flex',
                    gap: '8px',
                    borderTop: '1px solid #f3f4f6',
                    paddingTop: '14px'
                  }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(employee); }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <FaEdit style={{ fontSize: '13px' }} />
                      {language === 'ar' ? 'تعديل' : 'Edit'}
                    </button>
                    {canDelete && employee.role !== 'owner' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(employee.id); }}
                        style={{
                          padding: '10px 16px',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fee2e2';
                          e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fef2f2';
                          e.currentTarget.style.borderColor = '#fecaca';
                        }}
                      >
                        <FaTrash style={{ fontSize: '12px' }} />
                      {language === 'ar' ? 'حذف' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal + overlays */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '12px',
          }}
          onClick={() => {
            setShowModal(false);
            setShowZeroPermConfirm(false);
          }}
        >
          <div
            className="card"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            style={{
              maxWidth: '720px',
              width: '100%',
              maxHeight: '92vh',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              textAlign: language === 'ar' ? 'right' : 'left',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: '1px solid #e2e8f0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                flexShrink: 0,
                padding: '18px 20px 14px',
                background: 'linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)',
                borderBottom: '1px solid #fde68a',
              }}
            >
              <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
              {editingEmployee 
                  ? language === 'ar'
                    ? 'تعديل عضو الفريق'
                    : 'Edit team member'
                  : language === 'ar'
                    ? 'إضافة عضو جديد'
                    : 'Add team member'}
            </h2>
              {editingEmployee && formatEmpDate(editingEmployee.created_at) && (
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                  {language === 'ar' ? 'تاريخ إنشاء السجل: ' : 'Record created: '}
                  <span style={{ color: '#334155' }}>{formatEmpDate(editingEmployee.created_at)}</span>
                </p>
              )}
              {editingEmployee && editingEmployee.last_visit_date && (
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                  {language === 'ar' ? 'آخر تسجيل دخول: ' : 'Last sign-in: '}
                  <span style={{ color: '#334155' }}>
                    {formatLastSignInDisplay(editingEmployee.last_visit_date, language)}
                  </span>
                </p>
              )}
              {!editingEmployee && (
                <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#78716c' }}>
                  {language === 'ar' ? 'أنشئ الحساب ثم راجع الخطوات التالية.' : 'Create the account, then follow the steps below.'}
                </p>
              )}
              {isEmployeeWizard && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {[
                    { step: 1, ar: 'الملف', en: 'Profile' },
                    { step: 2, ar: 'الوصول', en: 'Access' },
                    { step: 3, ar: 'التفويض', en: 'Delegation' },
                  ].map(({ step, ar, en }, idx) => {
                    const active = modalStep === step;
                    const done = modalStep > step;
                    const canJump = step <= modalStep;
                    return (
                      <React.Fragment key={step}>
                        {idx > 0 && (
                          <div
                            style={{
                              width: '24px',
                              height: '2px',
                              alignSelf: 'center',
                              background: done || active ? '#f97316' : '#e5e7eb',
                              borderRadius: '2px',
                            }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (canJump && step < modalStep) setModalStep(step);
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '8px 14px',
                            borderRadius: '12px',
                            border: `2px solid ${active ? '#f97316' : done ? '#fdba74' : '#e5e7eb'}`,
                            background: active ? '#fff7ed' : done ? '#fffbeb' : '#f9fafb',
                            cursor: step < modalStep ? 'pointer' : 'default',
                            minWidth: '88px',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <span
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 800,
                              background: active ? '#f97316' : done ? '#fb923c' : '#e5e7eb',
                              color: active || done ? '#fff' : '#64748b',
                            }}
                          >
                            {done ? '✓' : step}
                          </span>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: active ? '#c2410c' : '#64748b' }}>
                            {language === 'ar' ? ar : en}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ flex: '1 1 auto', minHeight: 0, maxHeight: 'calc(92vh - 220px)', overflowY: 'auto', padding: '0 20px', WebkitOverflowScrolling: 'touch' }}>
              <form id="employee-modal-form" onSubmit={handleSubmit} noValidate>
                {/* Step 1 — Profile */}
                {(!isEmployeeWizard || modalStep === 1) && (
                  <div style={{ padding: '18px 0' }}>
                    <p
                      style={{
                        margin: '0 0 14px',
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#a8a29e',
                      }}
                    >
                      {language === 'ar' ? '١ — الهوية والتنظيم' : '1 — Identity & organization'}
                    </p>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                  {language === 'ar' ? 'الاسم' : 'Name'} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                          padding: '11px 12px',
                          border: `2px solid ${formErrors.name ? '#f87171' : '#e2e8f0'}`,
                          borderRadius: '10px',
                          fontSize: '1rem',
                          outline: 'none',
                          transition: 'border 0.15s',
                        }}
                      />
                      {formErrors.name && (
                        <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#dc2626' }}>{formErrors.name}</p>
                      )}
              </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'رقم الهاتف' : 'Phone'} *
                </label>
                      <div style={{ display: 'flex', gap: 8, direction: 'ltr' }}>
                        <select
                          value={phoneCountryCode}
                          onChange={e => setPhoneCountryCode(e.target.value)}
                          style={{
                            padding: '11px 8px', fontSize: '0.95rem', fontWeight: 600,
                            border: `2px solid ${formErrors.phone_number ? '#f87171' : '#e2e8f0'}`,
                            borderRadius: '10px', background: '#fff', cursor: 'pointer', flexShrink: 0, minWidth: 95
                          }}
                        >
                          {COUNTRY_CODES.map(c => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </option>
                          ))}
                        </select>
                <input
                  type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          dir="ltr"
                          placeholder={language === 'ar' ? 'مثال: 501234567' : 'e.g. 501234567'}
                  value={formData.phone_number}
                          onChange={(e) => setFormData({ ...formData, phone_number: e.target.value.replace(/\D/g, '') })}
                  style={{
                            flex: 1,
                            padding: '11px 12px',
                            border: `2px solid ${formErrors.phone_number ? '#f87171' : '#e2e8f0'}`,
                            borderRadius: '10px',
                            fontSize: '1rem',
                            textAlign: 'left',
                            unicodeBidi: 'plaintext',
                  }}
                />
              </div>
                      {formErrors.phone_number && (
                        <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#dc2626' }}>{formErrors.phone_number}</p>
                      )}
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                  {language === 'ar' ? 'كلمة المرور' : 'Password'} {!editingEmployee && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        autoComplete={editingEmployee ? 'new-password' : 'new-password'}
                  style={{
                    width: '100%',
                          padding: '11px 12px',
                          border: `2px solid ${formErrors.password ? '#f87171' : '#e2e8f0'}`,
                          borderRadius: '10px',
                          fontSize: '1rem',
                        }}
                      />
                      {formErrors.password && (
                        <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#dc2626' }}>{formErrors.password}</p>
                      )}
                {!editingEmployee && (
                        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#57534e' }}>
                          {language === 'ar' ? 'للدخول الأول — سيُطلب تغييرها لاحقاً من الحساب.' : 'For first sign-in — they can change it later in account settings.'}
                  </p>
                )}
              </div>
                    {!canCreateManager && editingEmployee && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                          {language === 'ar' ? 'الدور' : 'Role'}
                </label>
                        <div
                  style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '8px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e7e5e4',
                            background: '#fafaf9',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            color: formData.role === 'manager' ? '#92400e' : '#475569',
                          }}
                        >
                          {formData.role === 'manager'
                            ? (language === 'ar' ? 'مدير' : 'Manager')
                            : (language === 'ar' ? 'موظف' : 'Employee')}
              </div>
                        <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#78716c', lineHeight: 1.45 }}>
                          {language === 'ar'
                            ? 'تغيير الدور (مثل ترقية إلى مدير) يتولاه مالك الحساب فقط.'
                            : 'Changing role (e.g. to manager) is only available to the account owner.'}
                        </p>
                      </div>
                    )}
                    {canCreateManager && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b' }}>
                          {language === 'ar' ? 'الدور' : 'Role'}
                </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {[
                            {
                              value: 'employee',
                              label: language === 'ar' ? 'موظف' : 'Employee',
                              desc: language === 'ar' ? 'صلاحيات محددة في الخطوات التالية' : 'Limited access — set in next steps',
                            },
                            {
                              value: 'manager',
                              label: language === 'ar' ? 'مدير' : 'Manager',
                              desc: language === 'ar' ? 'وصول كامل للمنظمة' : 'Full org access',
                            },
                          ].map((r) => {
                            const active = formData.role === r.value;
                            return (
                              <button
                                key={r.value}
                                type="button"
                                onClick={() => {
                                  setFormData((fd) => {
                                    const nextRole = r.value;
                                    const nextDepts =
                                      String(nextRole).toLowerCase() === 'manager'
                                        ? Array.from(new Set([...(fd.departments || []), 'administration']))
                                        : fd.departments;
                                    return { ...fd, role: nextRole, departments: nextDepts };
                                  });
                                  if (r.value === 'manager') setModalStep(1);
                                }}
                                style={{
                                  flex: 1,
                                  padding: '12px',
                                  borderRadius: '12px',
                                  cursor: 'pointer',
                                  textAlign: language === 'ar' ? 'right' : 'left',
                                  border: `2px solid ${active ? '#f97316' : '#e5e7eb'}`,
                                  background: active ? '#fff7ed' : '#fafaf9',
                                }}
                              >
                                <div style={{ fontWeight: 700, color: active ? '#c2410c' : '#334155', fontSize: '0.92rem' }}>{r.label}</div>
                                <div style={{ fontSize: '0.78rem', color: '#78716c', marginTop: '4px', lineHeight: 1.35 }}>{r.desc}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b' }}>
                        {language === 'ar' ? 'الأقسام' : 'Departments'}
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {departments.map((dept) => {
                          const DeptIco = dept.icon;
                          const selected = formData.departments.includes(dept.value);
                          return (
                            <button
                              key={dept.value}
                              type="button"
                              onClick={() => {
                                const next = selected
                                  ? formData.departments.filter((d) => d !== dept.value)
                                  : [...formData.departments, dept.value];
                                setFormData({ ...formData, departments: next });
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '7px 14px',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                border: `2px solid ${selected ? dept.color : '#e5e7eb'}`,
                                background: selected ? `${dept.color}12` : '#fafaf9',
                                color: selected ? dept.color : '#78716c',
                                fontWeight: selected ? 700 : 500,
                                fontSize: '0.85rem',
                              }}
                            >
                              <DeptIco style={{ fontSize: '12px' }} />
                              {dept.label}
                              {selected && <FaTimes style={{ fontSize: '10px' }} />}
                            </button>
                          );
                        })}
                      </div>
                      {formData.departments.length === 0 && (
                        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#78716c' }}>
                          {formData.role === 'manager'
                            ? (language === 'ar'
                              ? 'يُضاف قسم «الإدارة» تلقائياً للمديرين.'
                              : 'Administration is included automatically for managers.')
                            : (language === 'ar'
                              ? 'لم يُختر قسم — يُستخدم «الإدارة» تلقائياً.'
                              : 'None selected — defaults to Administration.')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2 — Access (main permission groups) */}
                {isEmployeeWizard && modalStep === 2 && (
                  <div style={{ padding: '18px 0' }}>
                    <p
                      style={{
                        margin: '0 0 10px',
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#a8a29e',
                      }}
                    >
                      {language === 'ar' ? '٢ — الصلاحيات حسب النوع' : '2 — Access by feature'}
                    </p>
                    {countActivePermissions(formData.permissions) === 0 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          padding: '12px 14px',
                          borderRadius: '12px',
                          background: 'linear-gradient(90deg, #fffbeb 0%, #fef3c7 100%)',
                          border: '1px solid #fcd34d',
                          marginBottom: '14px',
                        }}
                      >
                        <FaInfoCircle style={{ color: '#d97706', marginTop: '2px', flexShrink: 0 }} />
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#78350f', lineHeight: 1.5 }}>
                          {language === 'ar'
                            ? 'لا توجد صلاحيات مفعّلة بعد — قد يظل الوصول محدوداً حتى تختار صلاحيات أدناه.'
                            : 'No permissions enabled yet — access may stay limited until you turn some on below.'}
                        </p>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                      {[
                        { key: 'all', ar: 'الكل', en: 'All' },
                        ...PERMISSION_GROUPS.map((g) => ({
                          key: g.key,
                          ar: g.label_ar,
                          en: g.label,
                        })),
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setPermFilter(opt.key)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '999px',
                            border: `2px solid ${permFilter === opt.key ? '#f97316' : '#e7e5e4'}`,
                            background: permFilter === opt.key ? '#fff7ed' : '#fff',
                            color: permFilter === opt.key ? '#c2410c' : '#57534e',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {language === 'ar' ? opt.ar : opt.en}
                        </button>
                      ))}
                    </div>
                    <div style={{ position: 'relative', marginBottom: '14px' }}>
                      <FaSearch
                        style={{
                          position: 'absolute',
                          [language === 'ar' ? 'right' : 'left']: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#a8a29e',
                          fontSize: '14px',
                        }}
                      />
                      <input
                        type="search"
                        placeholder={language === 'ar' ? 'تصفية الصلاحيات…' : 'Filter permissions…'}
                        value={permSearchQuery}
                        onChange={(e) => setPermSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                          padding: '10px 12px',
                          paddingLeft: language === 'ar' ? '12px' : '38px',
                          paddingRight: language === 'ar' ? '38px' : '12px',
                          border: '2px solid #e7e5e4',
                          borderRadius: '10px',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setPermissionsOpen((o) => !o)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        marginBottom: '10px',
                        borderRadius: '12px',
                        border: '2px solid #e7e5e4',
                        background: '#fafaf9',
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: '#1c1917',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaShieldAlt style={{ color: '#f97316' }} />
                        {language === 'ar' ? 'الصلاحيات' : 'Permissions'}
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '0.72rem',
                            background: countActivePermissions(formData.permissions) > 0 ? '#dcfce7' : '#fee2e2',
                            color: countActivePermissions(formData.permissions) > 0 ? '#15803d' : '#b91c1c',
                          }}
                        >
                          {countActivePermissions(formData.permissions)}/{ALL_PERMISSION_KEYS.length}
                        </span>
                      </span>
                      <span>{permissionsOpen ? '▲' : '▼'}</span>
                    </button>
                    {permissionsOpen && (
                      <div>
                        <div style={{ marginBottom: '14px' }}>
                          <p style={{ margin: '0 0 8px', fontSize: '0.78rem', fontWeight: 700, color: '#57534e' }}>
                            {language === 'ar' ? 'نموذج جاهز' : 'Presets'}
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                            {PERMISSION_PRESETS.map((preset) => (
                              <button
                                key={preset.key}
                                type="button"
                                onClick={() => setFormData((fd) => ({ ...fd, permissions: { ...preset.permissions } }))}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '6px 12px',
                                  borderRadius: '999px',
                                  border: '1.5px solid #e7e5e4',
                                  background: '#fff',
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                <span>{preset.icon}</span>
                                {language === 'ar' ? preset.label_ar : preset.label}
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setFormData((fd) => ({ ...fd, permissions: { ...EMPTY_PERMISSIONS } }))}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '999px',
                                border: '1.5px solid #fecaca',
                                background: '#fef2f2',
                                color: '#b91c1c',
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              {language === 'ar' ? 'مسح' : 'Clear'}
                            </button>
              </div>
                        </div>
                        {PERMISSION_GROUPS.filter((g) => permFilter === 'all' || g.key === permFilter).map((group) => {
                          const perms = group.permissions.filter((p) => permissionMatchesSearch(p));
                          if (!perms.length) return null;
                          return (
                            <div key={group.key} style={{ marginBottom: '16px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '10px',
                                  paddingBottom: '6px',
                                  borderBottom: `2px solid ${group.color}35`,
                                }}
                              >
                                <span
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: group.color,
                                  }}
                                />
                                <span style={{ fontWeight: 800, color: '#1c1917', fontSize: '0.88rem' }}>
                                  {language === 'ar' ? group.label_ar : group.label}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {perms.map((perm) => renderPermissionRow(perm, group))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3 — Delegation (optional, last) */}
                {isEmployeeWizard && modalStep === 3 && (
                  <div style={{ padding: '18px 0 24px' }}>
                    <p
                    style={{
                        margin: '0 0 10px',
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#a8a29e',
                      }}
                    >
                      {language === 'ar' ? '٣ — تفويض اختياري' : '3 — Optional delegation'}
                    </p>
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
                        border: '1px solid #c7d2fe',
                        marginBottom: '16px',
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '0.84rem', color: '#4338ca', lineHeight: 1.55, fontWeight: 600 }}>
                        {language === 'ar'
                          ? 'للتغطية أو تفويض شخص موثوق — صلاحيات أوسع على القنوات المتصلة.'
                          : 'Backup or trusted delegate — broader access to connected channels.'}
                      </p>
                </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {DELEGATION_GROUP.permissions.map((perm) =>
                        renderPermissionRow(perm, DELEGATION_GROUP, { sensitiveDelegation: true })
                      )}
                    </div>
                  </div>
                )}

                {/* Step 4 — Channel Access (only shown in edit mode, since we need user ID for grants) */}
                {isEmployeeWizard && modalStep === 4 && editingEmployee && (
                  <div style={{ padding: '18px 0 24px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#a8a29e' }}>
                      {language === 'ar' ? '٤ — صلاحية القنوات' : '4 — Channel access'}
                    </p>
                    <div style={{ padding: '14px 16px', borderRadius: '14px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #bbf7d0', marginBottom: '16px' }}>
                      <p style={{ margin: 0, fontSize: '0.84rem', color: '#166534', lineHeight: 1.55, fontWeight: 600 }}>
                        {language === 'ar'
                          ? 'حدّد أرقام واتساب التي يمكن لهذا الموظف رؤية محادثاتها. إذا لم يُختر أي رقم، يرى الموظف جميع الأرقام المتصلة.'
                          : 'Select which WhatsApp numbers this employee can see. If none are selected, they see all connected numbers.'}
                      </p>
                    </div>
                    {channelAccessLoading ? (
                      <p style={{ color: '#78716c', fontSize: '0.85rem' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading…'}</p>
                    ) : integrationNumbers.length === 0 ? (
                      <p style={{ color: '#78716c', fontSize: '0.85rem' }}>{language === 'ar' ? 'لا توجد أرقام متصلة' : 'No connected numbers'}</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {integrationNumbers.map((num) => {
                          const active = channelGrants.includes(num.id);
                          return (
                            <label
                              key={num.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                                background: active ? '#f0fdf4' : '#fafaf9',
                                border: `1px solid ${active ? '#86efac' : '#e7e5e4'}`,
                                transition: 'background 0.15s, border-color 0.15s',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => {
                                  setChannelGrants(prev =>
                                    prev.includes(num.id) ? prev.filter(g => g !== num.id) : [...prev, num.id]
                                  );
                                }}
                                style={{ accentColor: '#16a34a', width: '16px', height: '16px', cursor: 'pointer' }}
                              />
                              <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: 600, color: '#1c1917' }}>{num.label}</span>
                              <span style={{ fontSize: '0.75rem', color: '#a8a29e', fontFamily: 'monospace' }}>{num.id}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            {/* Sticky footer */}
            <div
              style={{
                flexShrink: 0,
                padding: '14px 20px 18px',
                borderTop: '1px solid #e7e5e4',
                background: 'linear-gradient(180deg, #fafaf9 0%, #ffffff 100%)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
                <button
                  type="button"
                onClick={() => {
                  setShowModal(false);
                  setShowZeroPermConfirm(false);
                }}
                  style={{
                  padding: '10px 18px',
                  borderRadius: '10px',
                  border: '2px solid #d6d3d1',
                  background: '#fff',
                  color: '#44403c',
                  fontWeight: 700,
                  cursor: 'pointer',
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                {!isEmployeeWizard && (
                <button
                  type="submit"
                    form="employee-modal-form"
                    style={{
                      padding: '10px 22px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                      color: '#fff',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
                    }}
                  >
                    <FaCheckCircle />
                    {editingEmployee ? (language === 'ar' ? 'حفظ' : 'Save') : language === 'ar' ? 'إنشاء' : 'Create'}
                  </button>
                )}
                {isEmployeeWizard && modalStep === 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (validateProfile()) setModalStep(2);
                    }}
                  style={{
                    padding: '10px 20px',
                      borderRadius: '10px',
                    border: 'none',
                      background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                      color: '#fff',
                      fontWeight: 800,
                    cursor: 'pointer',
                      display: 'inline-flex',
                    alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {language === 'ar' ? 'التالي' : 'Next'}
                    <NextIcon style={{ fontSize: '12px' }} />
                  </button>
                )}
                {isEmployeeWizard && modalStep === 2 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setModalStep(1)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '10px',
                        border: '2px solid #d6d3d1',
                        background: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <BackIcon style={{ fontSize: '12px' }} />
                      {language === 'ar' ? 'رجوع' : 'Back'}
                    </button>
                    <button
                      type="submit"
                      form="employee-modal-form"
                      style={{
                        padding: '10px 16px',
                        borderRadius: '10px',
                        border: '2px solid #fdba74',
                        background: '#fffbeb',
                        color: '#9a3412',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {editingEmployee ? (language === 'ar' ? 'حفظ الآن' : 'Save now') : language === 'ar' ? 'إنهاء وحفظ' : 'Save & finish'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalStep(3)}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        color: '#fff',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      {language === 'ar' ? 'التفويض' : 'Delegation'}
                      <NextIcon style={{ fontSize: '12px' }} />
                    </button>
                  </>
                )}
                {isEmployeeWizard && modalStep === 3 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setModalStep(2)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '10px',
                        border: '2px solid #d6d3d1',
                        background: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <BackIcon style={{ fontSize: '12px' }} />
                      {language === 'ar' ? 'رجوع' : 'Back'}
                    </button>
                    <button
                      type="submit"
                      form="employee-modal-form"
                      style={{
                        padding: '10px 22px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                        color: '#fff',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
                  }}
                >
                  <FaCheckCircle />
                      {editingEmployee ? (language === 'ar' ? 'حفظ التغييرات' : 'Save changes') : language === 'ar' ? 'إنشاء العضو' : 'Create member'}
                </button>
                    {editingEmployee && (
                      <button
                        type="button"
                        onClick={() => setModalStep(4)}
                        style={{
                          padding: '10px 18px', borderRadius: '10px', border: 'none',
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          color: '#fff', fontWeight: 800, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: '8px',
                        }}
                      >
                        {language === 'ar' ? 'القنوات' : 'Channels'}
                        <NextIcon style={{ fontSize: '12px' }} />
                      </button>
                    )}
                  </>
                )}
                {isEmployeeWizard && modalStep === 4 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setModalStep(3)}
                      style={{
                        padding: '10px 16px', borderRadius: '10px', border: '2px solid #d6d3d1',
                        background: '#fff', fontWeight: 700, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      <BackIcon style={{ fontSize: '12px' }} />
                      {language === 'ar' ? 'رجوع' : 'Back'}
                    </button>
                    <button
                      type="submit"
                      form="employee-modal-form"
                      style={{
                        padding: '10px 22px', borderRadius: '10px', border: 'none',
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        color: '#fff', fontWeight: 800, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                      }}
                    >
                      <FaCheckCircle />
                      {language === 'ar' ? 'حفظ التغييرات' : 'Save changes'}
                </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showZeroPermConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.5)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setShowZeroPermConfirm(false)}
        >
          <div
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '420px',
              width: '100%',
              borderRadius: '16px',
              padding: '22px',
              background: '#fff',
              border: '1px solid #fde68a',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ margin: '0 0 10px', fontSize: '1.05rem', color: '#78350f' }}>
              {language === 'ar' ? 'لم تُفعّل صلاحيات' : 'No permissions enabled'}
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: '0.9rem', color: '#57534e', lineHeight: 1.55 }}>
              {language === 'ar'
                ? 'سيُنشأ الحساب دون صلاحيات — المتابعة؟'
                : 'Account will be created with no permissions. Continue?'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowZeroPermConfirm(false)}
                style={{ padding: '10px 16px', borderRadius: '10px', border: '2px solid #d6d3d1', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                {language === 'ar' ? 'رجوع' : 'Go back'}
              </button>
              <button
                type="button"
                onClick={() => performSave({ forceZero: true })}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {language === 'ar' ? 'نعم، متابعة' : 'Yes, continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {delegationPrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(67,56,202,0.35)',
            zIndex: 1150,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setDelegationPrompt(null)}
        >
          <div
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '400px',
              width: '100%',
              borderRadius: '16px',
              padding: '22px',
              background: '#fff',
              border: '2px solid #a5b4fc',
            }}
          >
            <h3 style={{ margin: '0 0 10px', color: '#312e81', fontSize: '1.05rem' }}>
              {language === 'ar' ? 'صلاحية حساسة' : 'Sensitive access'}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#4c1d95', lineHeight: 1.55 }}>
              {language === 'ar'
                ? 'هذه الصلاحية تمنح وصولاً أوسع (إشراف أو إعدادات تكامل). تفعيلها فقط لمن يثق به فريقك.'
                : 'This grants broader access (oversight or integration settings). Enable only for people you trust.'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDelegationPrompt(null)}
                style={{ padding: '10px 16px', borderRadius: '10px', border: '2px solid #d6d3d1', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={confirmDelegationPrompt}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#4f46e5',
                  color: '#fff',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {language === 'ar' ? 'تفعيل' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {onboardingPayload && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            style={{
              maxWidth: '480px',
              width: '100%',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
              border: '1px solid #fed7aa',
            }}
          >
            <div
              style={{
                padding: '22px 24px',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)',
                color: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <FaClipboardList style={{ fontSize: '28px', opacity: 0.95 }} />
                <div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {language === 'ar' ? 'قائمة التهيئة' : 'Onboarding'}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>{onboardingPayload.name}</div>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '0.88rem', opacity: 0.95, lineHeight: 1.45 }}>
                {language === 'ar' ? 'تم إنشاء الحساب. أكمل الخطوات التالية مع العضو.' : 'Account created. Walk through these with your new member.'}
              </p>
            </div>
            <div style={{ padding: '20px 22px', background: '#fffef8' }}>
              {[
                {
                  title: language === 'ar' ? 'بيانات الدخول' : 'Sign-in details',
                  body: `${language === 'ar' ? 'الهاتف:' : 'Phone:'} ${onboardingPayload.phone}`,
                  action: (
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(onboardingPayload.phone)}
                      style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #fdba74',
                        background: '#fff7ed',
                        color: '#c2410c',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <FaCopy /> {language === 'ar' ? 'نسخ الرقم' : 'Copy phone'}
                    </button>
                  ),
                },
                {
                  title: language === 'ar' ? 'كلمة المرور' : 'Password',
                  body: onboardingPayload.passwordHint,
                },
                {
                  title: language === 'ar' ? 'الخطوة التالية' : 'Next step',
                  body: language === 'ar'
                    ? 'يُفضّل أن يسجّل العضو الدخول ويغيّر كلمة المرور من «معلومات الحساب».'
                    : 'They should sign in and change their password from account settings.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: i < 2 ? '14px' : 0,
                    padding: '14px 16px',
                    borderRadius: '14px',
                    border: '1px solid #e7e5e4',
                    background: i % 2 === 0 ? '#fff' : '#fafaf9',
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div style={{ fontWeight: 800, color: '#1c1917', marginBottom: '6px', fontSize: '0.95rem' }}>{item.title}</div>
                  <div style={{ fontSize: '0.88rem', color: '#57534e', lineHeight: 1.5 }}>{item.body}</div>
                  {item.action}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setOnboardingPayload(null)}
                style={{
                  width: '100%',
                  marginTop: '18px',
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(249,115,22,0.35)',
                }}
              >
                {language === 'ar' ? 'تم — فهمت' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmployeeManagement;